import dayjs from "dayjs";
import * as Yup from "yup";
import { OptionsDataType } from "../../../components";
import { STATUS_BADGE_TYPE } from "../../../components/statusBadge/StatusBadge";
import {
  BILLING_DETAILS_SITE_CORE_VALUE,
  DATE_FORMAT,
  EXTENDED_SPECIAL_CHARS,
  INPUT_LIMIT,
  PERSONAL_ID_REGEX,
  PROGRAM_CLOSURE_LANDED_SUCCESSFULLY_ID,
  REGEX_ACCEPTED_OPPORTUNITY_ID_SPECIAL_CHARS_AND_SPACES,
  REGEX_ACCEPTED_PO_NUMBER_SPECIAL_CHARS_AND_SPACES,
} from "../../../helpers/constants";
import {
  checkValueWithValidationRegex,
  dateWithValidation,
  notSameAsFieldTest,
  specialCharactersNotAllowedValidation,
  specialCharactersNotAllowedValidationForOptionalField,
  symmetricalFieldTest,
  validEmailValidation,
} from "../../../helpers/constants/commonValidationSchemaUtils";
import {
  mandatoryCountriesForAgeRange,
  mandatoryCountriesForCurrentEmploymentStartDate,
  mandatoryCountriesForDateOfBirth,
  mandatoryCountriesForEducationLevel,
  mandatoryCountriesForGender,
  mandatoryCountriesForNewEmployeeName,
  mandatoryCountriesForNewEmploymentContractType,
  mandatoryCountriesForNewTitle,
  mandatoryCountriesForOccupationalFunction,
  mandatoryCountriesForPersonalId,
  mandatoryCountriesForPostalCode,
  mandatoryCountriesForProgramClosureEndDate,
  mandatoryCountriesForProgramClosureFunctionalChange,
  mandatoryCountriesForProgramClosureIndustryChange,
  mandatoryCountriesForProgramClosurePostalCode,
  mandatoryCountriesForProgramClosureState,
  mandatoryCountriesForProgramCurrentSalaryRange,
  mandatoryCountriesForProgramGeographicalMove,
  mandatoryCountriesForProgramLandingCountry,
  mandatoryCountriesForProgramNewEmployerVATNumber,
  mandatoryCountriesForProgramNewLevel,
  mandatoryCountriesForProgramNewSalaryRange,
  mandatoryCountriesForSolutionValidationDate,
  mandatoryCountriesForStateRegionProvince,
  mandatoryCountriesForTotalYearsEmployed,
} from "../../../helpers/constants/countryFieldRulesUtils";
import { commonSiteCoreKeys } from "../../../helpers/constants/siteCoreKeys";
import { getValueOrBlank, isNotEmpty } from "../../../helpers/utils";
import { KeyValueMap } from "../../../redux/slices/appState/models";
import { CoachAssignmentFormKeys } from "../components/createProgramTabs/tabs/participant/components/coachAssignment/constants";
import { programSiteCoreKeys } from "../siteCore/SiteCoreKeys";
import {
  ADD_PROGRAM_FEE_AND_EXPENSE_TYPES,
  AddActivityFormKey,
  AddOrEditExpenseOrFeeFormKey,
  ClientManaged,
  CreateProgramFormKey,
  OccupationalFunctionOtherKey,
  OnsiteNotification,
  OutreachEligible,
  ParticipantTabFormKey,
  ProgramClosureFormKey,
  ProgramFeeTabFormKey,
  ProgramStatusFormKey,
  ProgramTabFormKey,
  VirtualNotification,
} from "./Models";

export const CreateProgramSchema = (commonValidationMessages: KeyValueMap) =>
  Yup.object().shape({
    [CreateProgramFormKey.client]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [CreateProgramFormKey.deliveryCountry]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [CreateProgramFormKey.lineOfBusiness]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [CreateProgramFormKey.programTemplate]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [CreateProgramFormKey.opportunityId]: Yup.string().test(
      "is-valid-opportunity-id",
      commonValidationMessages.specialCharNotAllowed,
      (value) =>
        !value ||
        !REGEX_ACCEPTED_OPPORTUNITY_ID_SPECIAL_CHARS_AND_SPACES.test(value),
    ),
    [CreateProgramFormKey.opportunityName]: Yup.string(),
    [CreateProgramFormKey.isOpportunityIdValidated]: Yup.boolean(),
    [CreateProgramFormKey.alternateName]: Yup.string().default(""),
    [CreateProgramFormKey.salesOfficeId]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [CreateProgramFormKey.nextPersona]: Yup.string().default(""),
    [CreateProgramFormKey.isProjectTagged]: Yup.boolean().default(false),
    [CreateProgramFormKey.projectKey]: Yup.string()
      .when(CreateProgramFormKey.isProjectTagged, {
        is: true,
        then: (schema) =>
          schema.required(commonValidationMessages.requiredValidation),
        otherwise: (schema) => schema.notRequired().default(""),
      })
      .default(""),
    [CreateProgramFormKey.projectName]: Yup.string().default(""),
  });

export const ProgramTabSchema = (
  commonValidationMessages: KeyValueMap,
  fetchValidationMessage: (key: string, replacements?: KeyValueMap) => string,
) =>
  Yup.object().shape({
    [ProgramTabFormKey.prefix]: Yup.string().default(""),
    [ProgramTabFormKey.firstName]: specialCharactersNotAllowedValidation(
      commonValidationMessages.requiredValidation,
      commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
    ).default(""),
    [ProgramTabFormKey.lastName]: specialCharactersNotAllowedValidation(
      commonValidationMessages.requiredValidation,
      commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
    ).default(""),
    [ProgramTabFormKey.programName]: Yup.string().default(""),
    [ProgramTabFormKey.solutionOffering]: Yup.string().default(""),
    [ProgramTabFormKey.programClass]: Yup.string().default(""),
    [ProgramTabFormKey.businessUnit]:
      specialCharactersNotAllowedValidationForOptionalField(
        commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
      ).default(""),
    [ProgramTabFormKey.costCentre]:
      specialCharactersNotAllowedValidationForOptionalField(
        commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
      ).default(""),
    [ProgramTabFormKey.clientInformation1]:
      specialCharactersNotAllowedValidationForOptionalField(
        commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
      ).default(""),
    [ProgramTabFormKey.clientInformation2]:
      specialCharactersNotAllowedValidationForOptionalField(
        commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
      ).default(""),
    [ProgramTabFormKey.clientInformation3]:
      specialCharactersNotAllowedValidationForOptionalField(
        commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
      ).default(""),
    [ProgramTabFormKey.site]:
      specialCharactersNotAllowedValidationForOptionalField(
        commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
      ).default(""),
    [ProgramTabFormKey.poNumber]: Yup.string().default(""),
    [ProgramTabFormKey.billingOffice]: Yup.string().default(""),
    [ProgramTabFormKey.salesOffice]: Yup.string().default(""),
    [ProgramTabFormKey.clientRelationShipOwner]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [ProgramTabFormKey.deliveryOffice]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [ProgramTabFormKey.billingContact]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [ProgramTabFormKey.hrContact]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [ProgramTabFormKey.projectManager]: Yup.string().default(""),
    [ProgramTabFormKey.projectCoordinator]: Yup.string().default(""),
    [ProgramTabFormKey.additionalInvoiceContactId]: Yup.string().default(""),
    [ProgramTabFormKey.participantManagerId]: Yup.string().default(""),
    [ProgramTabFormKey.salesCreditDetails]: Yup.array(),
    [ProgramTabFormKey.businessApprovalNumber]: Yup.string()
      .default("")
      .test(
        "is-valid-business-approval-number",
        commonValidationMessages.specialCharNotAllowed,
        (value) =>
          !value ||
          !REGEX_ACCEPTED_PO_NUMBER_SPECIAL_CHARS_AND_SPACES.test(value),
      ),
    [ProgramTabFormKey.additionalHRContactId]: Yup.string().default(""),
    [ProgramTabFormKey.deliveryLeadEngagementManagerId]:
      Yup.string().default(""),
    [ProgramTabFormKey.hiringManager]: Yup.string().default(""),
    [ProgramTabFormKey.dossierNumber]: Yup.string()
      .trim()
      .test(
        EXTENDED_SPECIAL_CHARS,
        fetchValidationMessage(
          commonSiteCoreKeys.fieldContainsInvalidCharactersErrorMessage,
        ),
        (value) =>
          !value ||
          INPUT_LIMIT.ALPHA_NUMERIC_WITH_EXTENDED_SPECIAL_CHARS.test(value),
      ),
  });

const applyCountryMandatoryRuleForLandedProgramForLandedProgram = <
  T extends Yup.StringSchema,
>(
  schema: T,
  deliveryCountry: string,
  mandatoryCountries: string[],
  requiredMessage: string,
) =>
  schema.when(
    [ProgramClosureFormKey.primaryClosingReason],
    ([primaryClosingReason], innerSchema) => {
      if (primaryClosingReason === PROGRAM_CLOSURE_LANDED_SUCCESSFULLY_ID) {
        return applyCountryMandatoryRule(
          innerSchema,
          deliveryCountry,
          mandatoryCountries,
          requiredMessage,
        );
      }
      return innerSchema.notRequired();
    },
  );

const applyCountryMandatoryRule = <T extends Yup.StringSchema>(
  schema: T,
  deliveryCountry: string,
  mandatoryCountries: string[],
  requiredMessage: string,
) =>
  schema.when([], {
    is: () => mandatoryCountries.includes(deliveryCountry),
    then: (innerSchema) => innerSchema.required(requiredMessage),
    otherwise: (innerSchema) => innerSchema.notRequired(),
  });

export const ParticipantTabSchema = (
  commonValidationMessages: KeyValueMap,
  userCountryDateFormat: string,
  isCMCProgram: boolean,
  isEmployeeIdRequired: boolean,
  isEmployeeBandRequired: boolean,
  deliveryCountry: string,
  isTMAssessmentProgram: boolean,
  isTMLeaderCoachingProgram: boolean,
  fetchValidationMessage: (key: string, replacements?: KeyValueMap) => string,
  isEditMode?: boolean,
  hasPermissionOutreach?: boolean,
  isMustStartDateDirty?: boolean,
) =>
  Yup.object().shape(
    {
      [ParticipantTabFormKey.prefix]: Yup.string().default(""),
      [ParticipantTabFormKey.firstName]: Yup.string()
        .required(commonValidationMessages.requiredValidation)
        .test(
          EXTENDED_SPECIAL_CHARS,
          fetchValidationMessage(
            commonSiteCoreKeys.fieldContainsInvalidCharactersErrorMessage,
          ),
          (value) =>
            !value ||
            INPUT_LIMIT.ALPHA_NUMERIC_WITH_EXTENDED_SPECIAL_CHARS.test(value),
        )
        .test(
          "no-only-space",
          commonValidationMessages.alphabetFieldValidator,
          (value) => {
            // Case 3: Check if the value is only spaces (after trimming)
            return !value || value.trim().length !== 0; // Invalid if only spaces
          },
        )
        .default(""),
      [ParticipantTabFormKey.lastName]: Yup.string()
        .required(commonValidationMessages.requiredValidation)
        .test(
          EXTENDED_SPECIAL_CHARS,
          fetchValidationMessage(
            commonSiteCoreKeys.fieldContainsInvalidCharactersErrorMessage,
          ),
          (value) =>
            !value ||
            INPUT_LIMIT.ALPHA_NUMERIC_WITH_EXTENDED_SPECIAL_CHARS.test(value),
        )
        .test(
          "no-only-space",
          commonValidationMessages.alphabetFieldValidator,
          (value) => {
            // Case 3: Check if the value is only spaces (after trimming)
            return !value || value.trim().length !== 0; // Invalid if only spaces
          },
        )
        .default(""),
      [ParticipantTabFormKey.preferredName]: Yup.string()
        .nullable()
        .test(
          "is-valid-spouse-name",
          commonValidationMessages.numberAndSpecialCharNotAllowed,
          (value) =>
            !value || INPUT_LIMIT.ONLY_ALPHABETS_WITH_SPACE.test(value),
        )
        .test(
          "no-only-space",
          commonValidationMessages.alphabetFieldValidator,
          (value) => {
            // allow empty but not only spaces
            return !value || value.trim().length !== 0;
          },
        )
        .default(null),
      [ParticipantTabFormKey.dateOfBirth]: applyCountryMandatoryRule(
        Yup.string(),
        deliveryCountry,
        mandatoryCountriesForDateOfBirth,
        commonValidationMessages.requiredValidation,
      ),
      [ParticipantTabFormKey.nextContactDate]: Yup.string()
        .nullable()
        .default(null),
      [ParticipantTabFormKey.age]: Yup.string().default(""),
      [ParticipantTabFormKey.gender]: applyCountryMandatoryRule(
        Yup.string(),
        deliveryCountry,
        mandatoryCountriesForGender,
        commonValidationMessages.requiredValidation,
      ),
      [ParticipantTabFormKey.spouse]:
        specialCharactersNotAllowedValidationForOptionalField(
          commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
        ).default(""),
      [ParticipantTabFormKey.educationLevel]: applyCountryMandatoryRule(
        Yup.string(),
        deliveryCountry,
        mandatoryCountriesForEducationLevel,
        commonValidationMessages.requiredValidation,
      ),
      [ParticipantTabFormKey.mustStartByDate]:
        !isEditMode || (isEditMode && isMustStartDateDirty)
          ? dateWithValidation(
              true,
              commonValidationMessages,
              true,
              false,
              userCountryDateFormat,
            )
          : dateWithValidation(
              true,
              commonValidationMessages,
              false,
              false,
              userCountryDateFormat,
            ),
      [ParticipantTabFormKey.primaryLanguage]: Yup.string().default(""),
      [ParticipantTabFormKey.socialFund]: Yup.string().default(""),
      [ParticipantTabFormKey.textSubscribedDate]: dateWithValidation(
        true,
        commonValidationMessages,
        true,
        false,
        userCountryDateFormat,
      ).when(
        [
          ParticipantTabFormKey.actualStartDate,
          ParticipantTabFormKey.projectedProgramEndDate,
        ],
        {
          is: (actualStartDate: string, projectedProgramEndDate: string) =>
            !!actualStartDate || !!projectedProgramEndDate,

          then: (schema) =>
            schema.test(
              "is-between-start-and-end",
              commonValidationMessages.subscribeValidationMessage,
              function (subscribedDate) {
                const programStartDate =
                  this.parent[ParticipantTabFormKey.actualStartDate];

                const programEndDate =
                  this.parent[ParticipantTabFormKey.projectedProgramEndDate];

                // No subscribed date → valid
                if (!subscribedDate) return true;

                const format =
                  userCountryDateFormat || DATE_FORMAT.YYYY_MM_DD_HYPHEN;

                const subscribed = dayjs(subscribedDate, format, true);
                const start = programStartDate
                  ? dayjs(programStartDate, format, true)
                  : null;
                const end = programEndDate
                  ? dayjs(programEndDate, format, true)
                  : null;

                // Invalid subscribed date
                if (!subscribed.isValid()) return false;

                // Must be after start date
                if (start?.isValid() && !subscribed.isAfter(start, "day")) {
                  return false;
                }

                // Must be before end date
                if (end?.isValid() && !subscribed.isBefore(end, "day")) {
                  return false;
                }

                return true;
              },
            ),
        },
      ),
      [ParticipantTabFormKey.textUnsubscribedDate]: dateWithValidation(
        true,
        commonValidationMessages,
        true,
        false,
        userCountryDateFormat,
      ).when([ParticipantTabFormKey.textSubscribedDate], {
        is: (subscribedDate: string) => !!subscribedDate,

        then: (schema) =>
          schema.test(
            "is-after-subscribed-date",
            commonValidationMessages.unsubscribeValidationMessage,
            function (unsubscribedDate) {
              const subscribedDate =
                this.parent[ParticipantTabFormKey.textSubscribedDate];

              // No unsubscribe date → valid
              if (!unsubscribedDate) return true;

              const format =
                userCountryDateFormat || DATE_FORMAT.YYYY_MM_DD_HYPHEN;

              const unsubscribed = dayjs(unsubscribedDate, format, true);
              const subscribed = subscribedDate
                ? dayjs(subscribedDate, format, true)
                : null;

              // Invalid unsubscribe date → invalid
              if (!unsubscribed.isValid()) return false;

              // Subscribed date missing or invalid → valid
              if (!subscribed?.isValid()) return true;

              // Must be after subscribed date
              return unsubscribed.isAfter(subscribed, "day");
            },
          ),
      }),
      [ParticipantTabFormKey.batchTypeKey]: Yup.string(),
      [ParticipantTabFormKey.waveCohort]: Yup.string(),
      [ParticipantTabFormKey.ageRange]: applyCountryMandatoryRule(
        Yup.string(),
        deliveryCountry,
        mandatoryCountriesForAgeRange,
        commonValidationMessages.requiredValidation,
      ),
      [ParticipantTabFormKey.personalId]: applyCountryMandatoryRule(
        Yup.string(),
        deliveryCountry,
        mandatoryCountriesForPersonalId,
        commonValidationMessages.requiredValidation,
      ).test(
        "alnum-end-letter",
        commonValidationMessages.personalIdValidationMessage,
        (value) => !value || PERSONAL_ID_REGEX.test(value),
      ),

      [ParticipantTabFormKey.nisId]: Yup.string()
        .test(
          "is-alphanumeric-or-empty",
          commonValidationMessages.alphanumericFieldValidator,
          (value) => !value || INPUT_LIMIT.ALPHA_NUMERIC_WITH_DASH.test(value),
        )
        .default(""),
      [ParticipantTabFormKey.address1]:
        specialCharactersNotAllowedValidationForOptionalField(
          commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
        ).default(""),
      [ParticipantTabFormKey.address2]:
        specialCharactersNotAllowedValidationForOptionalField(
          commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
        ).default(""),
      [ParticipantTabFormKey.address3]:
        specialCharactersNotAllowedValidationForOptionalField(
          commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
        ).default(""),
      [ParticipantTabFormKey.countryName]: Yup.string().default(""),
      [ParticipantTabFormKey.countryId]: Yup.string().default(""),
      [ParticipantTabFormKey.city]:
        specialCharactersNotAllowedValidationForOptionalField(
          commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
        ).default(""),
      [ParticipantTabFormKey.stateProvince]: Yup.string().when(
        ParticipantTabFormKey.countryId,
        (countryId, schema) => {
          const selectedCountry = Array.isArray(countryId)
            ? countryId[0]
            : countryId;

          return applyCountryMandatoryRuleForLandedProgramForLandedProgram(
            schema,
            selectedCountry,
            mandatoryCountriesForStateRegionProvince,
            commonValidationMessages.requiredValidation,
          ).test(
            EXTENDED_SPECIAL_CHARS,
            commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
            (value) =>
              checkValueWithValidationRegex(
                getValueOrBlank(value),
                INPUT_LIMIT.ALPHA_NUMERIC_WITH_EXTENDED_SPECIAL_CHARS,
              ),
          );
        },
      ),
      [ParticipantTabFormKey.postalCode]:
        applyCountryMandatoryRuleForLandedProgramForLandedProgram(
          Yup.string(),
          deliveryCountry,
          mandatoryCountriesForPostalCode,
          commonValidationMessages.requiredValidation,
        ),
      [ParticipantTabFormKey.eligibility]: Yup.string().required(
        commonValidationMessages.requiredValidation,
      ),
      [ParticipantTabFormKey.outreachEligibleStatus]: Yup.string()
        .when([ParticipantTabFormKey.eligibility], {
          is: (eligibilityValue: string) => {
            return eligibilityValue === OutreachEligible;
          },
          then: (schema) =>
            schema.required(commonValidationMessages.requiredValidation),
        })
        .default(""),
      [ParticipantTabFormKey.assignedOutreachConsultant]:
        Yup.string().default(""),

      [ParticipantTabFormKey.notificationDate]: Yup.string().when([], {
        is: () =>
          !isCMCProgram &&
          !isTMAssessmentProgram &&
          !isTMLeaderCoachingProgram &&
          hasPermissionOutreach,
        then: (schema) =>
          schema
            .required(commonValidationMessages.requiredValidation)
            .test(
              "is-before-outreach-eligible-date",
              commonValidationMessages.notificationDateErrorMessage,
              function (value) {
                const { outreachEligibleDate } = this.parent;

                if (!value || !outreachEligibleDate) return true;

                const notificationDate = dayjs(value, userCountryDateFormat);
                const outreachDate = dayjs(
                  outreachEligibleDate,
                  userCountryDateFormat,
                );
                if (!notificationDate.isValid() || !outreachDate.isValid())
                  return true;

                return notificationDate.isBefore(outreachDate);
              },
            ),
        otherwise: (schema) => schema.notRequired().default(""),
      }),

      [ParticipantTabFormKey.releaseDate]: Yup.string().default(""),
      [ParticipantTabFormKey.outreachEligibleDate]: Yup.string()
        .when([ParticipantTabFormKey.eligibility], {
          is: (eligibilityValue: string) => {
            return eligibilityValue === OutreachEligible;
          },
          then: (schema) =>
            schema.required(commonValidationMessages.requiredValidation),
        })
        .default(""),

      [ParticipantTabFormKey.notificationSupportStatus]: Yup.string().when([], {
        is: () =>
          !isCMCProgram &&
          !isTMAssessmentProgram &&
          !isTMLeaderCoachingProgram &&
          hasPermissionOutreach,
        then: (schema) =>
          schema.required(commonValidationMessages.requiredValidation),
        otherwise: (schema) => schema.notRequired().default(""),
      }),

      [ParticipantTabFormKey.notificationSupportDate]: Yup.string().when([], {
        is: () =>
          !isCMCProgram && !isTMAssessmentProgram && !isTMLeaderCoachingProgram,
        then: (schema) =>
          schema.when(ParticipantTabFormKey.notificationSupportStatus, {
            is: (val: string) =>
              val === VirtualNotification || val === OnsiteNotification,
            then: (innerSchema) =>
              innerSchema.required(commonValidationMessages.requiredValidation),
            otherwise: (innerSchema) => innerSchema.notRequired(),
          }),
        otherwise: (schema) => schema.notRequired().default(""),
      }),

      [ParticipantTabFormKey.primaryManagedStatus]: Yup.string()
        .when([ParticipantTabFormKey.eligibility], {
          is: (eligibilityValue: string) => {
            return eligibilityValue === ClientManaged;
          },
          then: (schema) =>
            schema.required(commonValidationMessages.requiredValidation),
        })
        .default(""),
      [ParticipantTabFormKey.secondaryManagedStatus]: Yup.string().default(""),

      [ParticipantTabFormKey.contactWithin24Hours]:
        Yup.boolean().default(false),
      [ParticipantTabFormKey.clientName]: Yup.string().default(""),
      [ParticipantTabFormKey.jobTitle]:
        specialCharactersNotAllowedValidationForOptionalField(
          commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
        ).default(""),
      [ParticipantTabFormKey.employeeId]: Yup.string()
        .nullable()
        .when([], {
          is: () => isEmployeeIdRequired,
          then: (schema) =>
            schema.required(commonValidationMessages.requiredValidation),
          otherwise: (schema) => schema.notRequired().nullable(),
        })
        .test(
          EXTENDED_SPECIAL_CHARS,
          fetchValidationMessage(
            commonSiteCoreKeys.fieldContainsInvalidCharactersErrorMessage,
          ),
          (value) =>
            !value ||
            INPUT_LIMIT.ALPHA_NUMERIC_WITH_EXTENDED_SPECIAL_CHARS.test(value),
        )
        .default(null),
      [ParticipantTabFormKey.employeeBand]: Yup.string()
        .nullable()
        .when([], {
          is: () => isEmployeeBandRequired,
          then: (schema) =>
            schema.required(commonValidationMessages.requiredValidation),
          otherwise: (schema) => schema.notRequired().nullable(),
        })
        .test(
          EXTENDED_SPECIAL_CHARS,
          fetchValidationMessage(
            commonSiteCoreKeys.fieldContainsInvalidCharactersErrorMessage,
          ),
          (value) =>
            !value ||
            INPUT_LIMIT.ALPHA_NUMERIC_WITH_EXTENDED_SPECIAL_CHARS.test(value),
        )
        .default(""),
      [ParticipantTabFormKey.totalYearsEmployed]: applyCountryMandatoryRule(
        Yup.string(),
        deliveryCountry,
        mandatoryCountriesForTotalYearsEmployed,
        commonValidationMessages.requiredValidation,
      ),
      [ParticipantTabFormKey.occupationalFunction]: applyCountryMandatoryRule(
        Yup.string(),
        deliveryCountry,
        mandatoryCountriesForOccupationalFunction,
        commonValidationMessages.requiredValidation,
      ),
      [ParticipantTabFormKey.occupationalFunctionOther]: Yup.string()
        .matches(
          INPUT_LIMIT.ALPHA_NUMERIC,
          commonValidationMessages.alphanumericFieldValidator,
        )
        .default(""),
      [ParticipantTabFormKey.occupationalLevel]: Yup.string().default(""),
      [ParticipantTabFormKey.occupationalLevelOther]: Yup.string()
        .matches(
          INPUT_LIMIT.ALPHA_NUMERIC,
          commonValidationMessages.alphanumericFieldValidator,
        )
        .default(""),
      [ParticipantTabFormKey.currenciesId]: Yup.string().optional(),
      [ParticipantTabFormKey.salaryRangeId]: Yup.string().optional(),
      [ParticipantTabFormKey.targetOccupationOtherFunction]: Yup.string().when(
        ParticipantTabFormKey.targetOccupationFunctionId,
        {
          is: (value: string) => value === OccupationalFunctionOtherKey, // Validate only when enabled
          then: (schema) =>
            schema
              .matches(
                INPUT_LIMIT.ALPHA_NUMERIC,
                commonValidationMessages.alphanumericFieldValidator,
              )
              .test(
                "no-only-spaces",
                commonValidationMessages.requiredValidation,
                (value) => !!value && value.trim().length > 0,
              )
              .required(commonValidationMessages.requiredValidation),
          otherwise: (schema) => schema.optional(), // No validation if disabled
        },
      ),
      [ParticipantTabFormKey.targetJobTitle]:
        specialCharactersNotAllowedValidationForOptionalField(
          commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
        ),
      [ParticipantTabFormKey.targetOrganisation]: Yup.string()
        .optional()
        .matches(
          INPUT_LIMIT.ALPHABETIC_WITH_SPACES_AND_HYPHEN_REGEX,
          commonValidationMessages.alphabetFieldValidator,
        ),
      [ParticipantTabFormKey.targetLocation]: Yup.string()
        .optional()
        .matches(
          INPUT_LIMIT.ALPHA_NUMERIC,
          commonValidationMessages.alphabetFieldValidator,
        ),
      [ParticipantTabFormKey.participantReadyToStartJobSearch]:
        Yup.string().test(
          isEditMode ? "always-valid" : "required-check",
          commonValidationMessages.requiredValidation,
          (value) => {
            if (isEditMode) {
              return true; // Skip validation in edit mode
            }
            return isNotEmpty(value);
          },
        ),

      [ParticipantTabFormKey.participantJobSearchStartDate]: Yup.string().when(
        ParticipantTabFormKey.participantReadyToStartJobSearch,
        {
          is: (value: string) =>
            value === programSiteCoreKeys.participantStartJobSearchYes &&
            isEditMode,
          then: (schema) =>
            schema
              .test(
                "required-check",
                commonValidationMessages.requiredValidation,
                (value) => !!value && value.trim().length > 0,
              )
              .required(commonValidationMessages.requiredValidation),
          otherwise: (schema) => schema.optional(), // No validation if disabled
        },
      ),

      [ParticipantTabFormKey.emailType1]: symmetricalFieldTest(
        ParticipantTabFormKey.emailType1,
        ParticipantTabFormKey.primaryEmail,
        commonValidationMessages.requiredValidation,
      ).default(""),

      [ParticipantTabFormKey.primaryEmail]: validEmailValidation(
        commonValidationMessages.invalidEmail,
      )
        .concat(
          symmetricalFieldTest(
            ParticipantTabFormKey.primaryEmail,
            ParticipantTabFormKey.emailType1,
            commonValidationMessages.requiredValidation,
          ),
        )
        .concat(
          notSameAsFieldTest(
            ParticipantTabFormKey.primaryEmail,
            ParticipantTabFormKey.alternativeEmail,
            commonValidationMessages.alreadyExistsEmailMessage,
          ),
        )
        .default(""),
      [ParticipantTabFormKey.emailType2]: symmetricalFieldTest(
        ParticipantTabFormKey.emailType2,
        ParticipantTabFormKey.alternativeEmail,
        commonValidationMessages.requiredValidation,
      ).default(""),

      [ParticipantTabFormKey.alternativeEmail]: validEmailValidation(
        commonValidationMessages.invalidEmail,
      )
        .concat(
          symmetricalFieldTest(
            ParticipantTabFormKey.alternativeEmail,
            ParticipantTabFormKey.emailType2,
            commonValidationMessages.requiredValidation,
          ),
        )
        .concat(
          notSameAsFieldTest(
            ParticipantTabFormKey.alternativeEmail,
            ParticipantTabFormKey.primaryEmail,
            commonValidationMessages.alreadyExistsEmailMessage,
          ),
        )
        .default(""),
      [ParticipantTabFormKey.concatType1]: symmetricalFieldTest(
        ParticipantTabFormKey.concatType1,
        ParticipantTabFormKey.primaryNumber,
        commonValidationMessages.requiredValidation,
      ).default(""),
      [ParticipantTabFormKey.primaryNumber]: symmetricalFieldTest(
        ParticipantTabFormKey.primaryNumber,
        ParticipantTabFormKey.concatType1,
        commonValidationMessages.requiredValidation,
      )
        .concat(
          notSameAsFieldTest(
            ParticipantTabFormKey.primaryNumber,
            ParticipantTabFormKey.alternativeNumber,
            commonValidationMessages.alreadyExistsNumberMessage,
          ),
        )
        .default(""),
      [ParticipantTabFormKey.concatType2]: symmetricalFieldTest(
        ParticipantTabFormKey.concatType2,
        ParticipantTabFormKey.alternativeNumber,
        commonValidationMessages.requiredValidation,
      ).default(""),
      [ParticipantTabFormKey.alternativeNumber]: symmetricalFieldTest(
        ParticipantTabFormKey.alternativeNumber,
        ParticipantTabFormKey.concatType2,
        commonValidationMessages.requiredValidation,
      )
        .concat(
          notSameAsFieldTest(
            ParticipantTabFormKey.alternativeNumber,
            ParticipantTabFormKey.primaryNumber,
            commonValidationMessages.alreadyExistsNumberMessage,
          ),
        )
        .default(""),
      [ParticipantTabFormKey.appendPhoneNumberCountryCode]:
        Yup.string().default(""),
      [ParticipantTabFormKey.appendPhoneNumberIsoCode]:
        Yup.string().default(""),
      [ParticipantTabFormKey.appendPhoneNumber]: Yup.string().default(""),
      [ParticipantTabFormKey.primaryIsoCode]: Yup.string().default(""),
      [ParticipantTabFormKey.primaryCountryCode]: Yup.string().default(""),
      [ParticipantTabFormKey.alternativeIsoCode]: Yup.string().default(""),
      [ParticipantTabFormKey.alternativeCountryCode]: Yup.string().default(""),
      [ParticipantTabFormKey.intakeStatusId]: Yup.string().when([], {
        is: () => isTMLeaderCoachingProgram,
        then: (schema) =>
          schema.required(commonValidationMessages.requiredValidation),
        otherwise: (schema) => schema.notRequired().default(""),
      }),
      [ParticipantTabFormKey.biosRequestedCoachIds]: Yup.array().optional(),
      [ParticipantTabFormKey.biosDeclinedCoachIds]: Yup.array().optional(),
      [ParticipantTabFormKey.bioStatusId]: Yup.string().optional(),
      [ParticipantTabFormKey.biosSentCoachIds]: Yup.array().optional(),
      [ParticipantTabFormKey.chemistryMeetingStatusId]: Yup.string().optional(),
      [ParticipantTabFormKey.chemistryMeetingSelectedCoachIds]:
        Yup.array().optional(),
      [ParticipantTabFormKey.coachSelectedCoachIds]: Yup.string().optional(),
      [ParticipantTabFormKey.isCoachSelectionDeclined]:
        Yup.boolean().optional(),

      [CoachAssignmentFormKeys.toBeAssignDate]: dateWithValidation(
        true,
        commonValidationMessages,
        false,
        false,
        userCountryDateFormat,
        true,
      ),
      [CoachAssignmentFormKeys.coachAssignments]: Yup.array().optional(),
      [CoachAssignmentFormKeys.coachAssignmentsId]: Yup.string().optional(),
      [ParticipantTabFormKey.datePlanApproved]: dateWithValidation(
        true,
        commonValidationMessages,
        false,
        false,
        userCountryDateFormat,
      ),
      [ParticipantTabFormKey.currentEmploymentStartDate]:
        applyCountryMandatoryRule(
          Yup.string(),
          deliveryCountry,
          mandatoryCountriesForCurrentEmploymentStartDate,
          commonValidationMessages.requiredValidation,
        ),
      [ParticipantTabFormKey.emailUnsubscribedDate]: dateWithValidation(
        true,
        commonValidationMessages,
        false,
        true,
        userCountryDateFormat,
        true,
      ),
    },
    [
      [ParticipantTabFormKey.dateOfBirth, ParticipantTabFormKey.dateOfBirth],
      [ParticipantTabFormKey.nisId, ParticipantTabFormKey.nisId],
    ],
  );

export const parseDate = (date: string, userCountryDateFormat: string) => {
  return date.includes("/") ? dayjs(date, userCountryDateFormat) : dayjs(date); // fallback to ISO or timestamp
};

export const isFutureDateValidation = (
  value: string | undefined,
  userCountryDateFormat: string,
) => {
  if (!value) return true;

  const inputDate = parseDate(value, userCountryDateFormat).format(
    DATE_FORMAT.YYYY_MM_DD_HYPHEN,
  );

  const today = dayjs().format(DATE_FORMAT.YYYY_MM_DD_HYPHEN);

  return dayjs(inputDate).isAfter(today) || dayjs(inputDate).isSame(today);
};

const isAfterDateValidation = (
  value: string | undefined | null,
  fromDate: string | undefined,
  userCountryDateFormat: string,
) => {
  if (!value || typeof value !== "string") return true;
  if (!fromDate || typeof fromDate !== "string") return true;

  const fromDateObj = parseDate(fromDate, userCountryDateFormat);
  const throughDateObj = parseDate(value, userCountryDateFormat);

  return dayjs(throughDateObj).isAfter(fromDateObj);
};

const isAfterAndEqualDateValidation = (
  value: string | undefined | null,
  fromDate: string | undefined,
  userCountryDateFormat: string,
) => {
  if (!value || typeof value !== "string") return true;
  if (!fromDate || typeof fromDate !== "string") return true;

  const fromDateObj = parseDate(fromDate, userCountryDateFormat);
  const throughDateObj = parseDate(value, userCountryDateFormat);

  return (
    dayjs(throughDateObj).isAfter(fromDateObj) ||
    dayjs(throughDateObj).isSame(fromDateObj)
  );
};

export const ChangeProgramStatusProgramSchema = (
  commonValidationMessages: KeyValueMap,
  isEditInactivateProgram: boolean,
  userCountryDateFormat: string,
  showEditForReopen: boolean,
) =>
  Yup.object().shape({
    [ProgramStatusFormKey.programStatus]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),

    [ProgramStatusFormKey.fromDate]: isEditInactivateProgram
      ? Yup.string().nullable().default("")
      : Yup.string()
          .test(
            "is-future-date",
            commonValidationMessages.dateShouldBeFuture,
            (value) => isFutureDateValidation(value, userCountryDateFormat),
          )
          .when(
            [ProgramStatusFormKey.programStatus],
            ([programStatus], schema) => {
              if (
                programStatus === STATUS_BADGE_TYPE.inActive ||
                programStatus === STATUS_BADGE_TYPE.reOpen
              ) {
                return schema.required(
                  commonValidationMessages.requiredValidation,
                );
              }
              return schema;
            },
          )
          .default(""),

    [ProgramStatusFormKey.throughDate]: Yup.string()
      .nullable()
      .when(
        [ProgramStatusFormKey.fromDate, ProgramStatusFormKey.programStatus],
        ([fromDate, programStatus], schema) => {
          const baseSchema = schema.test(
            "is-after-or-equal",
            commonValidationMessages.throughDateValidationMessage,
            (value) =>
              isAfterDateValidation(value, fromDate, userCountryDateFormat),
          );

          if (programStatus === STATUS_BADGE_TYPE.inActive) {
            return baseSchema.required(
              commonValidationMessages.requiredValidation,
            );
          }

          return baseSchema;
        },
      )
      .default(""),

    [ProgramStatusFormKey.projectedProgramEndDate]: Yup.string()
      .nullable()
      .when(
        [ProgramStatusFormKey.fromDate, ProgramStatusFormKey.programStatus],
        ([fromDate, programStatus], schema) => {
          const baseSchema = schema.test(
            "is-after-or-equal",
            commonValidationMessages.throughDateValidationMessage,
            (value) =>
              isAfterDateValidation(value, fromDate, userCountryDateFormat),
          );

          if (programStatus === STATUS_BADGE_TYPE.reOpen || showEditForReopen) {
            return baseSchema.required(
              commonValidationMessages.requiredValidation,
            );
          }

          return baseSchema;
        },
      )
      .default(""),

    [ProgramStatusFormKey.notes]: Yup.string()
      .when([ProgramStatusFormKey.programStatus], ([programStatus], schema) => {
        if (programStatus === STATUS_BADGE_TYPE.reOpen) {
          return schema.required(
            commonValidationMessages.notesRequiredValidationMessage,
          );
        }
        return schema;
      })
      .default(""),
  });

export const AddActivitySchema = (
  commonValidationMessages: KeyValueMap,
  isFromCoachDashboard?: boolean,
) =>
  Yup.object().shape({
    [AddActivityFormKey.chooseParticipant]: isFromCoachDashboard
      ? specialCharactersNotAllowedValidation(
          commonValidationMessages.requiredValidation,
          commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
        ).default("")
      : Yup.string().optional().default(""),
    [AddActivityFormKey.activityType]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [AddActivityFormKey.modality]: Yup.string()
      .default("")
      .required(commonValidationMessages.requiredValidation),
    [AddActivityFormKey.topic]: Yup.array()
      .of(Yup.string().required())
      .min(1, commonValidationMessages.requiredValidation)
      .default([]),
    [AddActivityFormKey.notes]: Yup.string().default(""),
    [AddActivityFormKey.onBehalfOfCoachId]: Yup.string().default(""),
    [AddActivityFormKey.actualDurationInMinutes]: Yup.string().default(""),
    [AddActivityFormKey.defaultDuration]: Yup.string().default(""),
    [AddActivityFormKey.activityDate]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
  });

export const ChangeProgramStatusClosed = (
  commonValidationMessages: KeyValueMap,
  secondaryClosingList: OptionsDataType[],
  deliveryCountry = "",
  userCountryDateFormat = "",
) =>
  Yup.object().shape({
    [ProgramClosureFormKey.primaryClosingReason]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [ProgramClosureFormKey.secondaryClosingReason]: Yup.string().when([], {
      is: () => isNotEmpty(secondaryClosingList),
      then: (schema) =>
        schema.required(commonValidationMessages.requiredValidation),
      otherwise: (schema) => schema.notRequired(),
    }),
    [ProgramClosureFormKey.closedDate]: Yup.string().default(""),
    [ProgramClosureFormKey.closedBy]: Yup.string().default(""),
    [ProgramClosureFormKey.landedDate]: Yup.string().default(""),
    [ProgramClosureFormKey.timeToLand]: Yup.string().default(""),
    [ProgramClosureFormKey.totalHours]: Yup.string().default(""),
    [ProgramClosureFormKey.totalNumberOfMeetings]: Yup.string().default(""),
    [ProgramClosureFormKey.geographicalMove]:
      applyCountryMandatoryRuleForLandedProgramForLandedProgram(
        Yup.string(),
        deliveryCountry,
        mandatoryCountriesForProgramGeographicalMove,
        commonValidationMessages.requiredValidation,
      ),
    [ProgramClosureFormKey.landingCountry]:
      applyCountryMandatoryRuleForLandedProgramForLandedProgram(
        Yup.string(),
        deliveryCountry,
        mandatoryCountriesForProgramLandingCountry,
        commonValidationMessages.requiredValidation,
      ),
    [ProgramClosureFormKey.state]:
      applyCountryMandatoryRuleForLandedProgramForLandedProgram(
        Yup.string(),
        deliveryCountry,
        mandatoryCountriesForProgramClosureState,
        commonValidationMessages.requiredValidation,
      ),
    [ProgramClosureFormKey.postalCode]:
      applyCountryMandatoryRuleForLandedProgramForLandedProgram(
        Yup.string(),
        deliveryCountry,
        mandatoryCountriesForProgramClosurePostalCode,
        commonValidationMessages.requiredValidation,
      ),
    [ProgramClosureFormKey.notes]: Yup.string().default(""),
    [ProgramClosureFormKey.newEmployeeName]:
      applyCountryMandatoryRuleForLandedProgramForLandedProgram(
        Yup.string(),
        deliveryCountry,
        mandatoryCountriesForNewEmployeeName,
        commonValidationMessages.requiredValidation,
      ),
    [ProgramClosureFormKey.newTitle]:
      applyCountryMandatoryRuleForLandedProgramForLandedProgram(
        Yup.string(),
        deliveryCountry,
        mandatoryCountriesForNewTitle,
        commonValidationMessages.requiredValidation,
      ),
    [ProgramClosureFormKey.contractType]: Yup.string().default(""),
    [ProgramClosureFormKey.functionalChange]:
      applyCountryMandatoryRuleForLandedProgramForLandedProgram(
        Yup.string(),
        deliveryCountry,
        mandatoryCountriesForProgramClosureFunctionalChange,
        commonValidationMessages.requiredValidation,
      ),
    [ProgramClosureFormKey.newOccupationalFunction]: Yup.string().when(
      ProgramClosureFormKey.functionalChange,
      {
        is: (functionalChange: string) =>
          functionalChange === programSiteCoreKeys.functionalChangeYes,
        // Only when functionalChange === "yes", apply the country rule:
        then: (schema) =>
          applyCountryMandatoryRuleForLandedProgramForLandedProgram(
            schema,
            deliveryCountry,
            mandatoryCountriesForProgramClosureFunctionalChange,
            commonValidationMessages.requiredValidation,
          ),
        // Otherwise, make sure it stays optional:
        otherwise: (schema) => schema.notRequired(),
      },
    ),

    [ProgramClosureFormKey.newOccupationalFunctionOther]:
      Yup.string().default(""),

    [ProgramClosureFormKey.newOccupationalLevel]: Yup.string().default(""),
    [ProgramClosureFormKey.industryChange]:
      applyCountryMandatoryRuleForLandedProgramForLandedProgram(
        Yup.string(),
        deliveryCountry,
        mandatoryCountriesForProgramClosureIndustryChange,
        commonValidationMessages.requiredValidation,
      ),
    [ProgramClosureFormKey.industryList]: Yup.string().default(""),
    [ProgramClosureFormKey.newJobStartDate]: Yup.string().default(""),
    [ProgramClosureFormKey.compensationComparison]: Yup.string().default(""),
    [ProgramClosureFormKey.currentSalaryRange]:
      applyCountryMandatoryRuleForLandedProgramForLandedProgram(
        Yup.string(),
        deliveryCountry,
        mandatoryCountriesForProgramCurrentSalaryRange,
        commonValidationMessages.requiredValidation,
      ),
    [ProgramClosureFormKey.newSalaryRange]:
      applyCountryMandatoryRuleForLandedProgramForLandedProgram(
        Yup.string(),
        deliveryCountry,
        mandatoryCountriesForProgramNewSalaryRange,
        commonValidationMessages.requiredValidation,
      ),
    [ProgramClosureFormKey.placementMethod]: Yup.string().default(""),
    [ProgramClosureFormKey.newEmployerVATNumber]:
      applyCountryMandatoryRuleForLandedProgramForLandedProgram(
        Yup.string(),
        deliveryCountry,
        mandatoryCountriesForProgramNewEmployerVATNumber,
        commonValidationMessages.requiredValidation,
      ),
    [ProgramClosureFormKey.newLevel]:
      applyCountryMandatoryRuleForLandedProgramForLandedProgram(
        Yup.string(),
        deliveryCountry,
        mandatoryCountriesForProgramNewLevel,
        commonValidationMessages.requiredValidation,
      ),
    [ProgramClosureFormKey.workLocationType]: Yup.string().default(""),
    [ProgramClosureFormKey.dateOfEndOfTrialPeriod]: Yup.string().when(
      ProgramClosureFormKey.newJobStartDate,
      {
        is: (newJobStartDate: string) => isNotEmpty(newJobStartDate),
        then: (schema) =>
          applyCountryMandatoryRuleForLandedProgramForLandedProgram(
            schema,
            deliveryCountry,
            mandatoryCountriesForProgramClosureEndDate,
            commonValidationMessages.requiredValidation,
          ),
        otherwise: (schema) => schema.notRequired(),
      },
    ),
    [ProgramClosureFormKey.solutionValidationDate]:
      applyCountryMandatoryRuleForLandedProgramForLandedProgram(
        Yup.string()
          .optional()
          .when([ProgramClosureFormKey.landedDate], ([landedDate], schema) => {
            return schema.test(
              "is-after-or-equal",
              commonValidationMessages.solutionValidationDateErrorMessage,
              (value) => {
                if (!value || !landedDate) return true;
                return isAfterAndEqualDateValidation(
                  value,
                  landedDate,
                  userCountryDateFormat,
                );
              },
            );
          }),
        deliveryCountry,
        mandatoryCountriesForSolutionValidationDate,
        commonValidationMessages.requiredValidation,
      ),
    [ProgramClosureFormKey.newEmploymentContractType]:
      applyCountryMandatoryRuleForLandedProgramForLandedProgram(
        Yup.string(),
        deliveryCountry,
        mandatoryCountriesForNewEmploymentContractType,
        commonValidationMessages.requiredValidation,
      ),
    [ProgramClosureFormKey.selectedPrimaryClosingReasonSitecoreKey]:
      Yup.string().default(""),
  });
export const ProgramFeeTabSchema = (
  commonValidationMessages: KeyValueMap,
  earlyPayPercentageErrorMessage: string,
  minimumTransferFeeShouldBeLessThanProgramFee: string,
  programFeeShouldBeGreaterThanMinimumTransferFee: string,
) =>
  Yup.object().shape(
    {
      [ProgramFeeTabFormKey.id]: Yup.string().default(""),
      [ProgramFeeTabFormKey.billingTypeId]: Yup.string().required(
        commonValidationMessages.requiredValidation,
      ),

      [ProgramFeeTabFormKey.billingTriggerId]: Yup.string().when(
        [ProgramFeeTabFormKey.billingTypeId],
        {
          is: (billingTypeId: string) =>
            ![
              BILLING_DETAILS_SITE_CORE_VALUE.billingTypeImmediateBilling,
              BILLING_DETAILS_SITE_CORE_VALUE.billingTypeSchedule,
              BILLING_DETAILS_SITE_CORE_VALUE.billingTypeMilestone,
            ].includes(billingTypeId),
          then: (schema) =>
            schema.required(commonValidationMessages.requiredValidation),
          otherwise: (schema) => schema.optional(),
        },
      ),
      [ProgramFeeTabFormKey.currencyId]: Yup.string().required(
        commonValidationMessages.requiredValidation,
      ),
      [ProgramFeeTabFormKey.fee]: Yup.string().required(
        commonValidationMessages.requiredValidation,
      ),
      [ProgramFeeTabFormKey.feeWithFormat]: Yup.string()
        .required(commonValidationMessages.requiredValidation)
        .test(
          "is-greater-than-minimum-transfer-fee",
          programFeeShouldBeGreaterThanMinimumTransferFee,
          (_, value) => {
            const programFeeValue = Number(value.parent?.fee ?? "0");
            const minimumTransferFeeValue = Number(
              value.parent?.minimumTransferFee ?? "0",
            );
            const proBonoCategoryId = value.parent?.proBonoCategoryId;
            const billingTypeId = value.parent?.billingTypeId;

            // If proBonoCategoryId has a value and billingTypeId is billingTypeImmediateBilling,
            // allow 0 or positive values only (skip comparison with minimumTransferFee)
            if (
              isNotEmpty(proBonoCategoryId) &&
              billingTypeId ===
                BILLING_DETAILS_SITE_CORE_VALUE.billingTypeImmediateBilling
            ) {
              return programFeeValue >= 0;
            }

            return programFeeValue > minimumTransferFeeValue;
          },
        ),

      [ProgramFeeTabFormKey.earlyPayDays]: Yup.string(),
      [ProgramFeeTabFormKey.earlyPayPercentage]: Yup.string().test(
        "is-less-than-10",
        earlyPayPercentageErrorMessage,
        (value) => {
          if (value === undefined || value === null || value === "") {
            return true;
          }
          const numberValue = Number(value);
          return !isNaN(numberValue) && numberValue < 10;
        },
      ),
      [ProgramFeeTabFormKey.minimumTransferFeeWithFormat]: Yup.string()
        .optional()
        .test(
          "is-smaller-than-fee",
          minimumTransferFeeShouldBeLessThanProgramFee,
          (_, value) => {
            if (!value.parent?.minimumTransferFee) return true;
            const programFeeValue = Number(value.parent?.fee ?? "0");
            const minimumTransferFeeValue = Number(
              value.parent?.minimumTransferFee ?? "0",
            );
            const proBonoCategoryId = value.parent?.proBonoCategoryId;
            const billingTypeId = value.parent?.billingTypeId;

            // If proBonoCategoryId has a value and billingTypeId is billingTypeImmediateBilling,
            // allow 0 or positive values only (skip comparison with minimumTransferFee)
            if (
              isNotEmpty(proBonoCategoryId) &&
              billingTypeId ===
                BILLING_DETAILS_SITE_CORE_VALUE.billingTypeImmediateBilling
            ) {
              return programFeeValue >= 0;
            }

            return programFeeValue > minimumTransferFeeValue;
          },
        ),
      [ProgramFeeTabFormKey.minimumTransferFee]: Yup.string().optional(),
      [ProgramFeeTabFormKey.proBonoCategoryId]: Yup.string().optional(),
      [ProgramFeeTabFormKey.specialBillingInstructions]:
        specialCharactersNotAllowedValidationForOptionalField(
          commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
        ),
      [ProgramFeeTabFormKey.schedulePeriod]: Yup.string().when(
        [ProgramFeeTabFormKey.billingTypeId],
        {
          is: (billingTypeId: string) =>
            [BILLING_DETAILS_SITE_CORE_VALUE.billingTypeSchedule].includes(
              billingTypeId,
            ),
          then: (schema) =>
            schema.required(commonValidationMessages.requiredValidation),
          otherwise: (schema) => schema.optional(),
        },
      ),
      [ProgramFeeTabFormKey.feePaymentType]: Yup.string().when(
        [ProgramFeeTabFormKey.billingTypeId],
        {
          is: (billingTypeId: string) =>
            [
              BILLING_DETAILS_SITE_CORE_VALUE.billingTypeMilestone,
              BILLING_DETAILS_SITE_CORE_VALUE.billingTypeSchedule,
            ].includes(billingTypeId),
          then: (schema) =>
            schema.required(commonValidationMessages.requiredValidation),
          otherwise: (schema) => schema.optional(),
        },
      ),
      [ProgramFeeTabFormKey.schedules]: Yup.string().optional(),
      [ProgramFeeTabFormKey.milestones]: Yup.string().optional(),
      [ProgramFeeTabFormKey.feePercentage]: Yup.string().optional(),
      [ProgramFeeTabFormKey.amount]: Yup.string().optional(),
      [ProgramFeeTabFormKey.amountWithFormat]: Yup.string().optional(),
      [ProgramFeeTabFormKey.scheduleFrequency]: Yup.string().optional(),

      [ProgramFeeTabFormKey.billingSchedules]: Yup.array(),
      [ProgramFeeTabFormKey.description]: Yup.string().optional(),
      [ProgramFeeTabFormKey.status]: Yup.string().optional(),
      [ProgramFeeTabFormKey.taxSearch]: Yup.string().optional(),
      [ProgramFeeTabFormKey.tax]: Yup.string().optional(),
      [ProgramFeeTabFormKey.taxAmount]: Yup.string().optional(),
      [ProgramFeeTabFormKey.taxAmountWithFormat]: Yup.string().optional(),
      [ProgramFeeTabFormKey.taxEntries]: Yup.array(),
    },
    [
      [
        ProgramFeeTabFormKey.feeWithFormat,
        ProgramFeeTabFormKey.minimumTransferFeeWithFormat,
      ],
    ],
  );

export const AddOrEditExpenseOrFeeSchema = (
  commonValidationMessages: KeyValueMap,
) => {
  const {
    feeOrExpenseName,
    type,
    transactionDate,
    feeTypeId,
    feeOrExpenseCurrencyId,
    feeOrExpenseAmountWithFormat,
    billingCurrency,
    billingCurrencyExchangeRate,
    comments,
    expenseIncurredById,
    expenseCategoryId,
    expenseUnitPrice,
    expenseQuantity,
    expenseTypeId,
  } = AddOrEditExpenseOrFeeFormKey;
  return Yup.object().shape({
    [type]: Yup.string(),
    [feeOrExpenseName]: specialCharactersNotAllowedValidation(
      commonValidationMessages.requiredValidation,
      commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
    ).default(""),
    [transactionDate]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    // fee details fields
    [feeTypeId]: Yup.string().when([type], {
      is: (type: string) =>
        ADD_PROGRAM_FEE_AND_EXPENSE_TYPES.PROGRAM_OR_PROJECT_FEE === type,
      then: (schema) =>
        schema.required(commonValidationMessages.requiredValidation),
      otherwise: (schema) => schema.optional(),
    }),
    [feeOrExpenseCurrencyId]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [feeOrExpenseAmountWithFormat]: Yup.string().when([type], {
      is: (type: string) =>
        ADD_PROGRAM_FEE_AND_EXPENSE_TYPES.PROGRAM_OR_PROJECT_FEE === type,
      then: (schema) =>
        schema.required(commonValidationMessages.requiredValidation),
      otherwise: (schema) => schema.optional(),
    }),
    // billing details
    [billingCurrency]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [billingCurrencyExchangeRate]: Yup.string().when(
      [feeOrExpenseCurrencyId, billingCurrency],
      {
        is: (feeOrExpenseCurrencyId: string, billingCurrency: string) => {
          if (
            feeOrExpenseCurrencyId &&
            feeOrExpenseCurrencyId !== billingCurrency
          ) {
            return true;
          }
          return false;
        },
        then: (schema) =>
          schema.required(commonValidationMessages.requiredValidation),
        otherwise: (schema) => schema.optional(),
      },
    ),
    // comments
    [comments]: specialCharactersNotAllowedValidationForOptionalField(
      commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
    ),
    // expense fields
    [expenseIncurredById]: Yup.string().optional(),
    [expenseCategoryId]: Yup.string().when(type, {
      is: (type: string) =>
        ADD_PROGRAM_FEE_AND_EXPENSE_TYPES.PROGRAM_EXPENSE === type,
      then: (schema) =>
        schema.required(commonValidationMessages.requiredValidation),
      otherwise: (schema) => schema.optional(),
    }),
    [expenseCategoryId]: Yup.string().when(type, {
      is: (type: string) =>
        ADD_PROGRAM_FEE_AND_EXPENSE_TYPES.PROGRAM_EXPENSE === type,
      then: (schema) =>
        schema.required(commonValidationMessages.requiredValidation),
      otherwise: (schema) => schema.optional(),
    }),
    [expenseQuantity]: Yup.string()
      .when(type, {
        is: (type: string) =>
          ADD_PROGRAM_FEE_AND_EXPENSE_TYPES.PROGRAM_EXPENSE === type,
        then: (schema) =>
          schema.required(commonValidationMessages.requiredValidation),
        otherwise: (schema) => schema.optional(),
      })

      .test(
        "greater-than-zero",
        commonValidationMessages.greaterThanZeroFieldValidator,
        (value) => {
          return !value || Number(value) > 0;
        },
      ),
    [expenseTypeId]: Yup.string().when(type, {
      is: (type: string) =>
        ADD_PROGRAM_FEE_AND_EXPENSE_TYPES.PROGRAM_EXPENSE === type,
      then: (schema) =>
        schema.required(commonValidationMessages.requiredValidation),
      otherwise: (schema) => schema.optional(),
    }),
    [expenseUnitPrice]: Yup.string()
      .when(type, {
        is: (type: string) =>
          ADD_PROGRAM_FEE_AND_EXPENSE_TYPES.PROGRAM_EXPENSE === type,
        then: (schema) =>
          schema.required(commonValidationMessages.requiredValidation),
        otherwise: (schema) => schema.optional(),
      })

      .test(
        "greater-than-zero",
        commonValidationMessages.greaterThanZeroFieldValidator,
        (value) => {
          return !value || Number(value) > 0;
        },
      ),
  });
};
